<template>
  <div
    id="main"
    class="flex items-start justify-center w-screen gap-12 overflow-hidden md:px-4 bg-concrete"
    :style="{
      paddingTop: $helpers.safeZone('top'),
      transform: 'translate(0)',
      height: $helpers.isPwaOpened() ? '100vh' : 'calc(var(--1svh) * 100)'
    }"
  >
    <!-- LEFT SIDEBAR -->
    <div
      id="left-sidebar"
      v-if="ui && desktop"
      class="hidden md:flex md:mt-6"
      :style="{height: `calc(100vh - ${desktop ? 1.5 : 0}rem)`}"
    >
      <slot name="left-sidebar">
        <t-sidebar-left />
      </slot>
    </div>
    <!-- LEFT SIDEBAR -->

    <!-- CONTENT WRAPPER -->
    <div
      id="content-wrapper"
      class="flex flex-col w-full md:w-desktop md:mt-6"
      :class="pageWrapperClass"
      :style="
        anchor && anchor === 'content-wrapper'
          ? {height: `calc(100% - ${desktop ? 1.5 : 0}rem)`, transform: 'translate(0)'}
          : {height: `calc(100% - ${desktop ? 1.5 : 0}rem)`}
      "
    >
      <!-- HEADER -->
      <div id="header-wrapper" class="flex flex-col justify-center" v-if="ui">
        <slot name="header">
          <t-header :title="userCurrentModule && userCurrentModule.name" @clickTitle="emitContentReload()">
            <template v-if="menuStyle !== 'ontop' || isDesktop" v-slot:left-side><div></div></template>
            <template v-slot:right-side>
              <div v-if="menuStyle === 'ontop'" class="w-6 h-6"></div>
              <div v-else></div>
            </template>
          </t-header>
        </slot>

        <!-- DYNAMIC ISLAND -->
        <div
          class="w-full px-2 mt-1 overflow-hidden duration-200"
          :class="{'h-0': Island._hidden, 'h-10': !Island._hidden}"
          v-if="$route.name !== 'networking.speed' && $route.name !== 'networking.group' && Island && Island._params"
        >
          <div
            class="flex items-center justify-between h-full px-4 bg-black rounded-lg cursor-pointer"
            @click="
              $router.push({
                name: `networking.${Island._params.mode === 'speedNetworking' ? 'speed' : 'group'}`,
                params: {app_module_id: userNetworkingModule.app_module_id}
              })
            "
          >
            <div class="flex items-center gap-2">
              <div class="live-indicator">
                <div class="live-pulse-min"></div>
                <div class="live-pulse-max"></div>
                <span class="live-indicator__circle"></span>
              </div>
              <t-text class="font-semibold" color="white"
                >{{ Island._params.mode === 'speedNetworking' ? 'Speed' : 'Group' }} Networking Activated</t-text
              >
            </div>
            <ph-arrow-right class="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
      <!-- HEADER -->

      <!-- CONTENT -->
      <main id="content" ref="content" :class="pageClass" :style="pageStyle">
        <slot></slot>
      </main>
      <!-- CONTENT -->

      <!-- FOOTER -->
      <div
        id="footer-wrapper"
        ref="footer"
        v-if="ui && !Footer._hidden"
        class="fixed z-50 flex self-center justify-between w-full max-w-desktop md:px-2 bg-concrete"
        style="bottom: 0px"
      >
        <slot name="footer">
          <t-footer v-if="mobile && menuStyle === 'classic'" />
        </slot>
      </div>
      <!-- FOOTER -->
    </div>
    <!-- CONTENT WRAPPER -->

    <!-- RIGHT SIDEBAR -->
    <div
      id="right-sidebar"
      v-if="ui && wideDesktop"
      class="hidden md:flex md:mt-6"
      :style="{height: `calc(100vh - ${desktop ? 1.5 : 0}rem)`}"
    >
      <slot name="right-sidebar">
        <t-sidebar-right></t-sidebar-right>
      </slot>
    </div>
    <!-- RIGHT SIDEBAR -->
  </div>
</template>

<script>
  import handleViewport from '@/utils/mixins/handleViewport'
  import {mapGetters} from 'vuex'
  import {mapMutations} from 'vuex'
  export default {
    name: 't-main',
    props: {
      type: {
        type: String,
        default: 'default',
        validator: v => ['default', 'card', 'auth', 'scroll-bottom', 'nopb'].indexOf(v) !== -1
      },
      anchor: {
        type: String
      }
    },
    mixins: [handleViewport],
    computed: {
      ...mapGetters(['menuStyle', 'theme']),
      ...mapGetters('layout', ['Footer', 'Header', 'layoutChanged', 'Island']),
      ...mapGetters('user', [
        'menuLoaded',
        'userFirstModule',
        'userMenu',
        'userCurrentModule',
        'userNetworkingModule',
        'userHideIosAlert',
        'userHideAndroidAlert'
      ]),
      currentRouteModule() {
        return this.userMenu && this.userMenu.find(el => el.app_module_id == this.$route.app_module_id)
      },
      ui() {
        return !this.$route.name.split('.').includes('auth')
      },
      desktop() {
        return this.viewport.width >= this.$screen.md
      },
      wideDesktop() {
        return this.viewport.width >= this.$screen.lg
      },
      mobile() {
        return this.viewport.width < this.$screen.md
      },
      pageClass() {
        const classes = {
          'default':
            'scrollbar-hidden flex self-center w-full flex-col items-center justify-start max-w-desktop md:px-2 gap-4 md:pt-4 sm:pt-4 p-4 pb-25 overflow-y-scroll overflow-x-hidden',
          'nopb':
            'scrollbar-hidden flex self-center w-full flex-col items-center justify-start max-w-desktop md:px-2 gap-4 md:pt-4 sm:pt-4 p-4 pb-0 overflow-y-scroll overflow-x-hidden',
          'auth':
            'flex flex-col items-center md:max-w-[30rem] justify-start md:bg-white bg-transparent gap-8 relative md:mt-auto md:mb-auto md:self-center md:min-w-[30rem] md:w-full rounded-lg px-4 md:px-15 md:max-h-[45rem] md:border md:border-platinum overflow-y-scroll scrollbar-hidden',
          'scroll-bottom':
            'scrollbar-hidden flex self-center w-full flex-col-reverse items-center justify-start max-w-desktop md:px-2 gap-4 md:pt-4 sm:pt-4 p-4 pb-4 overflow-y-scroll overflow-x-hidden'
        }

        return classes[this.type]
      },
      pageWrapperClass() {
        const classes = {
          'default': '',
          'auth': '',
          'scroll-bottom': 'translate-x-0 translate-y-0'
        }

        return classes[this.type]
      },
      pageStyle() {
        const styles = {
          'default': {height: `calc(100vh - ${this.headerHeight}px - ${this.footerHeight}px)`},
          'nopb': {height: `calc(100vh - ${this.headerHeight}px - ${this.footerHeight}px)`},
          'auth': {
            height:
              this.viewport.height < this.viewport.width
                ? `calc(var(--1svh) * 100 - 3rem)`
                : this.viewport.width >= this.$screen.md
                ? `calc(100vh - 8.75rem)`
                : `calc(var(--1svh) * 100)`,
            paddingTop: this.viewport.height < this.viewport.width ? '1rem' : `calc(4rem + ${this.$helpers.safeZone('top')})`
          },
          'scroll-bottom': {height: `calc(100% - ${this.headerHeight}px - ${this.footerHeight}px)`}
        }

        return styles[this.type]
      },
      prioritizedBubble() {
        const prioritizedBubble = this.$helpers.getPrioritizedBubbleFromMenuEntities(this.userMenu)
        const bubble = this.userMenu.find(el => el.type === prioritizedBubble)
        if (bubble) return bubble.bubble.length
        else return 0
      },
      prioritizedBubbleType() {
        const prioritizedBubble = this.$helpers.getPrioritizedBubbleFromMenuEntities(this.userMenu)
        const bubble = this.userMenu.find(el => el.type === prioritizedBubble)
        if (bubble) return this.$helpers.getBubbleTypeFromMenuType(bubble.type)
        else return 'dot'
      },
      installIos() {
        return this.userMenu.find(el => el.type === 'installIos')
      },
      installAndroid() {
        return this.userMenu.find(el => el.type === 'installAndroid')
      }
    },
    data() {
      return {
        headerHeight: 0,
        footerHeight: 0,
        island: {
          isVisible: false,
          data: null
        }
      }
    },
    async mounted() {
      await this.menuLoaded
      this.$nextTick(() => this.initLayout())
    },
    watch: {
      'Footer._hidden'() {
        this.$nextTick(() => this.initLayout())
      },
      'Header._hidden'() {
        this.$nextTick(() => this.initLayout())
      },
      'layoutChanged'() {
        this.$nextTick(() => {
          this.initLayout()
        })
      }
    },
    methods: {
      ...mapMutations('layout', ['emitContentReload']),
      ...mapMutations('user', ['handleInstallPrompt']),
      onDesktopViewEnter() {
        this.$nextTick(() => this.initLayout())
      },
      onMobileViewEnter() {
        this.$nextTick(() => this.initLayout())
      },
      goToMenu(item) {
        if (this.$route.path.split('/').includes(String(item.app_module_id))) return this.emitContentReload()
        this.$router.push({
          name: `${item.type}.index`,
          params: {
            app_module_id: item.app_module_id
          }
        })
      },
      async initLayout() {
        await this.$helpers.sleep(10)
        const header = document.querySelector('header')
        const footer = document.querySelector('footer')

        if (header) {
          this.headerHeight = header.clientHeight
        } else {
          this.headerHeight = 0
        }
        if (footer) {
          this.footerHeight = footer.clientHeight
        } else {
          this.footerHeight = 0
        }
        this.$nextTick(() => this.$forceUpdate())
      }
    }
  }
</script>
